import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import VideoUpload from '../components/VideoUpload';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Video, 
  Image as ImageIcon,
  Hash, 
  Send,
  Loader2,
  X,
  Upload
} from 'lucide-react';

import { API_URL } from '../utils/api';

export default function CreateCreatorPostPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const imageInputRef = useRef(null);
  
  const [mediaType, setMediaType] = useState('video'); // 'video' or 'image'
  const [caption, setCaption] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Check if user can post
  const canPost = user && (
    user.subscription_status === 'active' || 
    user.subscription_status === 'trial' ||
    user.is_admin ||
    user.is_tradie
  );

  if (!user) {
    navigate('/login');
    return null;
  }

  if (!canPost) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl p-8 max-w-md text-center shadow-sm">
          <Video className="mx-auto h-16 w-16 text-stone-300 mb-4" />
          <h2 className="text-xl font-bold text-stone-800 mb-2">
            Subscription Required
          </h2>
          <p className="text-stone-600 mb-6">
            You need an active seller subscription or tradie profile to share your work.
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600"
            >
              Start Free Trial
            </Button>
            <Button
              onClick={() => navigate('/creator')}
              variant="outline"
              className="w-full"
            >
              Back to Showroom
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    if (tag && !tags.includes(tag) && tags.length < 10) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('Image must be under 20MB');
      return;
    }

    setUploadingImage(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/upload/showroom-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setImageUrl(data.media_url);
        toast.success('Image uploaded!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasMedia = mediaType === 'video' ? videoUrl : imageUrl;
    if (!hasMedia) {
      toast.error(`Please upload a ${mediaType}`);
      return;
    }

    if (!caption.trim()) {
      toast.error('Please add a caption');
      return;
    }

    setSubmitting(true);

    try {
      const postData = {
        caption: caption.trim(),
        media_type: mediaType,
        tags: tags
      };

      if (mediaType === 'video') {
        postData.video_url = videoUrl;
        postData.thumbnail_url = thumbnailUrl;
      } else {
        postData.media_url = imageUrl;
      }

      const response = await fetch(`${API_URL}/api/creator-posts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        toast.success('Posted successfully!');
        navigate('/creator');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post');
      }
    } catch (error) {
      console.error('Post error:', error);
      toast.error('Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const suggestedTags = ['woodworking', 'restoration', 'furniture', 'handmade', 'diy', 'upcycling', 'painting', 'metalwork'];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/creator')}
              className="flex items-center text-stone-600 hover:text-stone-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </button>
            <h1 className="text-lg font-semibold text-stone-800">Share Your Work</h1>
            <div className="w-20" />
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Media Type Toggle */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Label className="text-stone-700 font-medium mb-3 block">
              What would you like to share?
            </Label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setMediaType('image')}
                className={`flex-1 py-4 px-4 rounded-lg border-2 transition-all ${
                  mediaType === 'image' 
                    ? 'border-teal-500 bg-teal-50 text-teal-600' 
                    : 'border-stone-200 hover:border-stone-300 text-stone-600'
                }`}
              >
                <ImageIcon className="h-8 w-8 mx-auto mb-2" />
                <span className="block text-sm font-medium">Photo</span>
                <span className="block text-xs text-stone-500">Up to 20MB</span>
              </button>
              <button
                type="button"
                onClick={() => setMediaType('video')}
                className={`flex-1 py-4 px-4 rounded-lg border-2 transition-all ${
                  mediaType === 'video' 
                    ? 'border-teal-500 bg-teal-50 text-teal-600' 
                    : 'border-stone-200 hover:border-stone-300 text-stone-600'
                }`}
              >
                <Video className="h-8 w-8 mx-auto mb-2" />
                <span className="block text-sm font-medium">Video</span>
                <span className="block text-xs text-stone-500">Up to 150MB</span>
              </button>
            </div>
          </div>

          {/* Media Upload */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            {mediaType === 'video' ? (
              <VideoUpload
                onVideoUploaded={setVideoUrl}
                onThumbnailUploaded={setThumbnailUrl}
                existingVideo={videoUrl}
                existingThumbnail={thumbnailUrl}
              />
            ) : (
              <div>
                <Label className="text-stone-700 font-medium mb-3 block">
                  <ImageIcon className="inline h-5 w-5 mr-2" />
                  Photo (up to 20MB)
                </Label>
                
                {imageUrl ? (
                  <div className="relative">
                    <img 
                      src={imageUrl.startsWith('/api') ? `${API_URL}${imageUrl}` : imageUrl}
                      alt="Uploaded"
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setImageUrl(null)}
                      className="absolute top-2 right-2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="image-upload-input"
                    className="block border-2 border-dashed border-teal-300 rounded-lg p-12 text-center cursor-pointer hover:border-teal-500 hover:bg-teal-50/50 transition-colors"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-12 w-12 mx-auto text-teal-400 animate-spin mb-4" />
                    ) : (
                      <Upload className="h-12 w-12 mx-auto text-teal-400 mb-4" />
                    )}
                    <p className="text-stone-600 font-medium">
                      {uploadingImage ? 'Uploading...' : 'Click to upload photo'}
                    </p>
                    <p className="text-stone-500 text-sm mt-1">
                      JPEG, PNG, WebP, HEIC (max 20MB)
                    </p>
                  </label>
                )}
                
                <input
                  id="image-upload-input"
                  ref={imageInputRef}
                  type="file"
                  accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif"
                  onChange={handleImageUpload}
                  className="sr-only"
                />
              </div>
            )}
          </div>

          {/* Caption */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Label htmlFor="caption" className="text-stone-700 font-medium">
              Caption *
            </Label>
            <Textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Tell viewers about this creation... What inspired you? How did you make it?"
              className="mt-2 min-h-[100px]"
              maxLength={1000}
            />
            <p className="text-xs text-stone-500 mt-1 text-right">
              {caption.length}/1000
            </p>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <Label className="text-stone-700 font-medium">
              <Hash className="inline h-4 w-4 mr-1" />
              Tags
            </Label>
            <p className="text-sm text-stone-500 mb-3">
              Add tags to help people discover your work
            </p>
            
            {/* Tag Input */}
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagKeyDown}
                placeholder="Add a tag"
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                maxLength={20}
              />
              <Button
                type="button"
                onClick={addTag}
                variant="outline"
                disabled={!tagInput.trim() || tags.length >= 10}
              >
                Add
              </Button>
            </div>

            {/* Current Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {tags.map(tag => (
                  <span 
                    key={tag}
                    className="inline-flex items-center gap-1 bg-teal-100 text-teal-600 px-3 py-1 rounded-full text-sm"
                  >
                    #{tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="hover:text-teal-700"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Suggested Tags */}
            <div className="flex flex-wrap gap-2">
              {suggestedTags.filter(t => !tags.includes(t)).map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setTags([...tags, tag].slice(0, 10))}
                  className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full hover:bg-stone-200"
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={submitting || (!videoUrl && !imageUrl) || !caption.trim()}
            className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 py-6 text-lg"
          >
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Posting...
              </>
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Share to Showroom
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
